import { forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import {
  FlexColumn,
  TextField,
  Dialog,
} from '@elementary/common-ui/components';
import FormControls from '@elementary/common-ui/components/FormControls';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { UserInviteForm } from 'shared/types';
import { isValidEmail } from 'shared/utils/validators';
import { Role } from '../shared/types';
import RoleSelectFormField from './RoleSelectFormField';

const Wrapper = styled(FlexColumn)`
  gap: 24px;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirmButtonClick: ({ name, email, role_id }: UserInviteForm) => void;
  cancelText?: string;
  confirmText: string;
  errorMessage?: string;
  ref?: any;
  inviteUserLoading?: boolean;
  roles?: Role[];
};

const ElementaryModal = forwardRef(
  (
    {
      open,
      onClose,
      onConfirmButtonClick,
      cancelText = 'Cancel',
      confirmText,
      errorMessage,
      inviteUserLoading,
      roles,
    }: Props,
    ref: any,
  ) => {
    const methods = useForm<UserInviteForm>({ mode: 'onBlur' });
    const {
      register,
      handleSubmit,
      formState: { errors, isDirty, isValid },
      reset,
    } = methods;

    useImperativeHandle(ref, () => ({
      reset: () => reset(),
    }));

    const onSubmit: SubmitHandler<{
      name: string;
      email: string;
      role_id: string;
    }> = (data) => {
      onConfirmButtonClick(data);
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        onConfirmButtonClick={() => handleSubmit(onSubmit)()}
        title="Invite user"
        cancelText={cancelText}
        confirmText={confirmText}
        isLoading={inviteUserLoading}
        confirmDisabled={!isDirty || !isValid}
        message={
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Wrapper>
                <FormControls.InputWrapper>
                  <TextField
                    {...register('name', {
                      required: 'This field is required',
                    })}
                    variant="outlined"
                    size="large"
                    label="Full Name"
                  />
                  {!!errors.name?.message && (
                    <FormControls.ErrorMessage variant="caption">
                      {errors.name?.message}
                    </FormControls.ErrorMessage>
                  )}
                </FormControls.InputWrapper>

                <FormControls.InputWrapper>
                  <TextField
                    {...register('email', {
                      required: 'This field is required',
                      validate: (value) =>
                        isValidEmail(value) ||
                        'Please enter a valid email address',
                    })}
                    variant="outlined"
                    size="large"
                    label="Email"
                  />
                  {!!errors.email?.message && (
                    <FormControls.ErrorMessage variant="caption">
                      {errors.email?.message}
                    </FormControls.ErrorMessage>
                  )}
                </FormControls.InputWrapper>

                <FormControls.InputWrapper>
                  <RoleSelectFormField roles={roles} />
                  {!!errors.role_id?.message && (
                    <FormControls.ErrorMessage variant="caption">
                      {errors.role_id?.message}
                    </FormControls.ErrorMessage>
                  )}
                </FormControls.InputWrapper>

                <FormControls.InputWrapper>
                  {!!errorMessage && (
                    <FormControls.ErrorMessage variant="caption">
                      <div>{errorMessage}</div>
                    </FormControls.ErrorMessage>
                  )}
                </FormControls.InputWrapper>
              </Wrapper>
            </form>
          </FormProvider>
        }
      />
    );
  },
);

export default ElementaryModal;
